<script setup lang="ts">
import { computed, watchEffect, inject, Ref, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSteps } from '/@src/composable/useSteps'
import * as apisPortal from '/@src/client-portal/api'
import { NazvyKrokov } from '/@src/utils/zoppm/types'
import { useFormValues } from 'vee-validate'
import { formatDateString } from '/@src/utils/format'
import { Subjekt, ZoPPM } from '/@src/client-portal'
import { ZoppmSchemeTransformer } from '/@src/utils/zoppm'
import { NazvyAssemblySekcii } from '/@src/utils/zoppm/ZoppmSchemeTransformer/types'

export interface IdentifikaciaZiadatelaPo {}

defineProps<IdentifikaciaZiadatelaPo>()

const { t } = useI18n()
const steps = useSteps()
const formValues = useFormValues()
const getApi = new apisPortal.SubjektyApi()
const organizaciaData = ref()
const bankoveSpojenie = ref()
const subjektDetail = inject<Ref<Subjekt>>('subjektDetail')
const stavPodana = inject<boolean>('stavPodana', false)
const zoppmDetail = inject<Ref<ZoPPM>>('zoppmDraftDetail')

const getUcet = async (id: string, bankSpojId: string) =>
  await getApi.portalGetSubjektBankSpojSubjekty(id, bankSpojId).then((t) => t.data)

const korespondencnaAdresa = computed(() => {
  return {
    nazov: t('zoppm.zhrnutie.identifikaciaZiadatela.pravnickaOsoba.korespondencnaAdresa'),
    hodnota: formValues.value?.ziadatel?.korespondencnaAdresa,
  }
})

watchEffect(async () => {
  const bankoveSpojenieData =
    subjektDetail?.value?.subjektRef?.subjektId &&
    formValues.value?.ziadatel?.bankoveSpojenie?.bankoveSpojenieRef?.bankoveSpojenieId
      ? await getUcet(
          subjektDetail?.value?.subjektRef?.subjektId,
          formValues.value?.ziadatel?.bankoveSpojenie?.bankoveSpojenieRef
            ?.bankoveSpojenieId
        )
      : null

  bankoveSpojenie.value = {
    nazov: t('zoppm.zhrnutie.identifikaciaZiadatela.pravnickaOsoba.bankoveSpojenie'),
    hodnota:
      bankoveSpojenieData?.iban ||
      zoppmDetail?.data?.ziadatel?.bankoveSpojenie?.bankoveSpojenie?.iban
        ? [
            {
              nazov: '',
              hodnota:
                bankoveSpojenieData?.banka?.nazov ||
                zoppmDetail?.data?.ziadatel?.bankoveSpojenie?.bankoveSpojenie?.banka
                  ?.nazov,
            },
            {
              nazov: t('zoppm.zhrnutie.identifikaciaZiadatela.pravnickaOsoba.iban'),
              hodnota:
                bankoveSpojenieData?.iban ||
                zoppmDetail?.data?.ziadatel?.bankoveSpojenie?.bankoveSpojenie?.iban,
            },
            {
              nazov: t('zoppm.zhrnutie.identifikaciaZiadatela.pravnickaOsoba.bic'),
              hodnota:
                bankoveSpojenieData?.banka?.bic ||
                zoppmDetail?.data?.ziadatel?.bankoveSpojenie?.bankoveSpojenie?.banka?.bic,
            },
            {
              nazov: t('zoppm.zhrnutie.identifikaciaZiadatela.pravnickaOsoba.mena'),
              hodnota:
                bankoveSpojenieData?.mena?.nazov ||
                zoppmDetail?.data?.ziadatel?.bankoveSpojenie?.bankoveSpojenie?.mena
                  ?.nazov,
            },
          ]
        : [],
  }

  organizaciaData.value = [
    {
      nazov: t('zoppm.zhrnutie.identifikaciaZiadatela.fyzickaOsoba.menoAPriezvisko'),
      hodnota: stavPodana
        ? zoppmDetail?.data?.ziadatel?.subjektRef?.nazov
        : subjektDetail?.value?.osoba?.menoUplne,
    },
  ]
  organizaciaData.value.push({
    nazov: t('zoppm.zhrnutie.identifikaciaZiadatela.fyzickaOsoba.datumNarodenia'),
    hodnota:
      formatDateString(subjektDetail?.value?.osoba?.datumNarodenia) ||
      zoppmDetail?.data?.ziadatel?.osoba?.osobaRef?.datumNarodenia,
  })
  organizaciaData.value.push({
    nazov: 'Email',
    hodnota:
      subjektDetail?.value?.osoba?.primarnyKontakt?.email ||
      zoppmDetail?.data?.ziadatel?.osoba?.primarnyKontakt.email,
  })
  organizaciaData.value.push({
    nazov: 'Telefónne číslo',
    hodnota:
      subjektDetail?.value?.osoba?.primarnyKontakt?.telefonneCislo ||
      zoppmDetail?.data?.ziadatel?.osoba?.primarnyKontakt?.telefonneCislo,
  })

  organizaciaData.value.push({
    nazov: t('zoppm.zhrnutie.identifikaciaZiadatela.fyzickaOsoba.adresaTrvalehoPobytu'),
    hodnota:
      subjektDetail?.value?.osoba?.primarnaAdresa?.adresaUplna ||
      zoppmDetail.data?.ziadatel?.osoba?.primarnaAdresa.adresaUplna,
    stat:
      subjektDetail?.value?.osoba?.primarnaAdresa?.stat?.nazov ||
      zoppmDetail.data?.ziadatel?.osoba?.primarnaAdresa?.stat?.nazov,
    isAddress: true,
  })
})

const schemeTransformer = inject<Ref<ZoppmSchemeTransformer>>('schemeTransformer')
const sekciaRefNazov: NazvyAssemblySekcii = 'zoppm.ss.ziadatel-fo'
const heading = computed(
  () =>
    schemeTransformer?.value.getSectionTitle(sekciaRefNazov) ||
    t('zoppm.zhrnutie.identifikaciaZiadatela.mainHeading')
)
const isEnabled = computed(() =>
  schemeTransformer?.value.isSectionEnabled(sekciaRefNazov)
)
</script>

<template>
  <div v-if="isEnabled">
    <IDSKHeading as="h2" variant="l" class="mb-7"> {{ heading }} </IDSKHeading>
    <DisplayList bold="title" inline-layout="one-half" full-width class="mb-5">
      <template v-for="(item, key) in organizaciaData" :key="key">
        <template v-if="item.hodnota">
          <dt>{{ item.nazov }}</dt>
          <dd>
            <tempalte v-if="!item.isAddress">
              {{ item.hodnota }}
            </tempalte>
            <template v-else
              ><Address :data="item.hodnota" /> <br />
              <Address :data="item.stat"
            /></template>
          </dd>
        </template>
      </template>
    </DisplayList>

    <DisplayList
      v-if="korespondencnaAdresa?.hodnota"
      bold="title"
      inline-layout="one-half"
      full-width
      class="mb-5"
    >
      <dt>
        {{ korespondencnaAdresa?.nazov }}
      </dt>
      <dd><Address :data="korespondencnaAdresa?.hodnota" /></dd>
    </DisplayList>

    <div v-if="!stavPodana" class="d-flex align-items-right">
      <IDSKButton
        variant="secondary"
        type="button"
        :data-test-id="`zoppm-identifikacia-ziadatela-fo-edit`"
        @click="
          () =>
            steps.setStep(NazvyKrokov['identifikaciaZiadatela'], {
              ref: NazvyKrokov['zhrnutie'],
            })
        "
      >
        {{ t('zoppm.zhrnutie.editBtn') }}
      </IDSKButton>
    </div>

    <template v-if="bankoveSpojenie?.hodnota?.length > 0">
      <DisplayList bold="title" inline-layout="one-half" full-width class="mb-5">
        <dt>
          {{ bankoveSpojenie?.nazov }}
        </dt>
        <dd>
          <p v-for="item in bankoveSpojenie?.hodnota" :key="item" class="mb-0">
            {{ item?.nazov }} {{ item?.hodnota }}
          </p>
        </dd>
      </DisplayList>

      <div v-if="!stavPodana" class="d-flex align-items-right">
        <IDSKButton
          variant="secondary"
          type="button"
          class="mb-0"
          :data-test-id="`zoppm-identifikacia-ziadatela-fo-edit`"
          @click="
            () =>
              steps.setStep(NazvyKrokov['identifikaciaZiadatela'], {
                ref: NazvyKrokov['zhrnutie'],
              })
          "
        >
          {{ t('zoppm.zhrnutie.editBtn') }}
        </IDSKButton>
      </div>
    </template>

    <hr class="mb-8 mt-8" />
  </div>
</template>
